import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Code2, Database, Palette, Shield, Sparkles } from "lucide-react";

export default function BootcampProgramsSection() {
  const navigate = useNavigate();

  const programs = [
    {
      id: "data",
      name: "Data Science & AI",
      description: "Master Data Analytics, Machine Learning, and AI to drive the future of business intelligence globally.",
      duration: "12 Weeks",
      format: "Live Online",
      icon: <Database size={28} className="text-[#d15424]" />,
      tags: ["Python", "SQL", "TensorFlow"],
      bgImg: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80"
    },
    {
      id: "engineering",
      name: "Software Engineering",
      description: "Build highly scalable web and mobile applications from scratch using modern frameworks.",
      duration: "16 Weeks",
      format: "Live Online",
      icon: <Code2 size={28} className="text-[#d15424]" />,
      tags: ["React", "Node.js", "System Design"],
      bgImg: "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?auto=format&fit=crop&w=800&q=80"
    },
    {
      id: "product",
      name: "Product Design (UI/UX)",
      description: "Learn human-centered design, prototyping, and research to create products that millions love.",
      duration: "10 Weeks",
      format: "Live Online",
      icon: <Palette size={28} className="text-[#d15424]" />,
      tags: ["Figma", "User Research", "Prototyping"],
      bgImg: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=800&q=80"
    },
    {
      id: "cyber",
      name: "Cybersecurity",
      description: "Defend enterprise networks and organizations from sophisticated digital threats worldwide.",
      duration: "14 Weeks",
      format: "Live Online",
      icon: <Shield size={28} className="text-[#d15424]" />,
      tags: ["Ethical Hacking", "CompTIA", "Cryptography"],
      bgImg: "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?auto=format&fit=crop&w=800&q=80"
    }
  ];

  return (
    <Box sx={{ py: 20, bgcolor: "white", position: "relative" }}>
      {/* Subtle Background Elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#d15424] opacity-[0.03] rounded-full blur-[120px] pointer-events-none"></div>

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 10 }}>
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8">
            <Box sx={{ maxWidth: "700px" }}>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-gray-50 rounded-full border border-gray-200 mb-6">
                <Sparkles size={14} className="text-[#d15424]" />
                <span className="text-xs font-bold text-gray-700 font-montserrat uppercase tracking-wider">Premium Programs</span>
              </div>
              <Typography variant="h2" sx={{ fontWeight: 900, color: "#0f172a", fontSize: { xs: "2.5rem", md: "3.5rem" }, mb: 4, letterSpacing: "-0.03em", fontFamily: "Monserrat, sans-serif", lineHeight: 1.1 }}>
                World-class education, <br/>built for the <span className="text-[#d15424]">industry.</span>
              </Typography>
              <Typography variant="body1" sx={{ color: "#475569", fontFamily: "Monserrat, sans-serif", fontSize: "1.15rem", lineHeight: 1.6 }}>
                Join our intensive, cohort-based learning schools. Engineered by global tech leaders, tailored for ambitious individuals, and rigorously designed to get you hired anywhere in the world.
              </Typography>
            </Box>
            <div className="shrink-0">
                <button 
                  onClick={() => navigate('/courses')}
                  className="px-6 py-3 bg-white text-[#0f172a] font-bold rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.05)] border border-gray-200 hover:shadow-[0_8px_20px_rgba(0,0,0,0.08)] hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-2 font-montserrat"
                >
                    View all programs <ArrowRight size={18} />
                </button>
            </div>
        </div>

        {/* CSS Grid for Bento-like layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {programs.map((prog, index) => (
                <motion.div
                    key={prog.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ delay: index * 0.1, duration: 0.6, ease: "easeOut" }}
                    className="group relative rounded-[2.5rem] overflow-hidden bg-white border border-gray-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_20px_50px_rgba(0,0,0,0.12)] transition-all duration-500 cursor-pointer flex flex-col sm:flex-row min-h-[320px]"
                    onClick={() => navigate(`/courses`)}
                >
                    {/* Left/Top: Content */}
                    <div className="w-full sm:w-[55%] p-8 md:p-10 flex flex-col justify-between order-2 sm:order-1 relative z-20 bg-white">
                        <div>
                            <div className="w-14 h-14 rounded-2xl bg-orange-50 border border-orange-100 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                                {prog.icon}
                            </div>
                            <h3 className="font-black text-2xl md:text-3xl text-[#0f172a] mb-3 font-montserrat tracking-tight group-hover:text-[#d15424] transition-colors">{prog.name}</h3>
                            <p className="text-[#475569] font-montserrat text-sm md:text-base leading-relaxed mb-6 font-medium">{prog.description}</p>
                        </div>
                        
                        <div>
                            <div className="flex flex-wrap gap-2 mb-8">
                                {prog.tags.map(tag => (
                                    <span key={tag} className="bg-gray-50 text-gray-600 border border-gray-200 text-xs font-bold px-3 py-1.5 rounded-lg font-montserrat">{tag}</span>
                                ))}
                            </div>
                            <div className="flex items-center text-[#d15424] font-bold text-sm gap-2 group-hover:gap-3 transition-all font-montserrat">
                                Explore Program <ArrowRight size={18} />
                            </div>
                        </div>
                    </div>

                    {/* Right/Bottom: Image */}
                    <div className="w-full sm:w-[45%] h-64 sm:h-auto relative overflow-hidden order-1 sm:order-2">
                      <div className="absolute inset-0 bg-gradient-to-t sm:bg-gradient-to-l from-transparent via-transparent to-white z-10 w-full h-[50%] sm:h-full sm:w-[30%]"></div>
                      <div className="absolute inset-0 bg-[#0f172a]/20 group-hover:bg-transparent transition-colors duration-500 z-0"></div>
                      <img src={prog.bgImg} alt={prog.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 origin-center" />
                      
                      {/* Floating Duration Badge */}
                      <div className="absolute top-6 right-6 z-20 bg-white/95 backdrop-blur-md px-4 py-2 rounded-xl text-sm font-bold text-[#0f172a] shadow-lg font-montserrat border border-white/20">
                        {prog.duration}
                      </div>
                    </div>
                </motion.div>
            ))}
        </div>
      </Container>
    </Box>
  );
}
